import { useTranslation } from "react-i18next";
import { cn } from "../../../lib/utils";
import { useActiveLang } from "../../../domains/app/hooks/useActiveLang";
import { getCountriesListSortedByPopularity } from "../../../domains/app/utils/countries";

/**
 * The profile the registry found, on the desktop details screen (Figma
 * "D7.identite.pays-selectionne"): the answers the practitioner gave, and the
 * role and the establishment the registry adds to them.
 *
 * Read only. The name, the role and the establishment are the record's, as
 * the registry spells them, so a namesake or a mistyped number shows as
 * someone else; the country is the one the registry was searched in. Continue
 * confirms the record as it stands, and "Fill in my profile manually" says it
 * is someone else.
 * A field the record does not carry is not drawn.
 */
export function RegistryFoundDetails({
  firstName,
  lastName,
  country,
  roleLabel,
  establishment,
}: {
  firstName: string;
  lastName: string;
  /** ISO code. */
  country: string;
  /** The Juisci profession the record maps to, or the record's own label. */
  roleLabel: string | null;
  establishment: string | null;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { activeLang } = useActiveLang();

  const countryOption = getCountriesListSortedByPopularity(activeLang).find(
    (option) => option.value === country
  );
  if (countryOption === undefined)
    throw new Error(`Invariant: a registry was searched for ${country}`);

  return (
    <div className="flex w-full flex-col gap-6">
      <div className="grid w-full grid-cols-2 gap-3">
        <ReadOnlyField
          label={t("First Name")}
          value={firstName}
          shape="input"
        />
        <ReadOnlyField label={t("Last Name")} value={lastName} shape="input" />
      </div>
      <ReadOnlyField
        label={t("Country")}
        value={`${countryOption.flag}  ${countryOption.name}`}
        shape="select"
      />
      {roleLabel !== null && (
        <ReadOnlyField label={t("Role")} value={roleLabel} shape="select" />
      )}
      {establishment !== null && (
        <ReadOnlyField
          label={t("Name of the establishment")}
          value={establishment}
          shape="input"
        />
      )}
    </div>
  );
}

function ReadOnlyField({
  label,
  value,
  shape,
}: {
  label: string;
  value: string;
  /** The height of the field it stands for: an `Input` or a `Select`. */
  shape: "input" | "select";
}): React.JSX.Element {
  return (
    <div className="flex w-full min-w-0 flex-col gap-1">
      <span className="font-dm-sans text-ui-note text-ds-black">{label}</span>
      <p
        className={cn(
          "m-0 flex items-center truncate whitespace-pre rounded-xl border border-ds-slate-200 bg-ds-white px-4 font-dm-sans text-ui-body text-ds-slate-950",
          shape === "input" ? "h-13" : "h-12"
        )}
      >
        {value}
      </p>
    </div>
  );
}
